import { useQuery } from '@tanstack/react-query';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

async function fetchApi(endpoint, options = {}) {
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: {
      'Accept': 'application/json',
      ...options.headers,
    },
    // credentials: 'include',
    ...options,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export function useEntityTypes() {
  return useQuery({
    queryKey: ['entityTypes'],
    queryFn: async () => {
      const data = await fetchApi('/entity_types');
      return data.map(item => item.entity_type);
    },
  });
}

export function useEntitiesByType(type) {
  return useQuery({
    queryKey: ['entities', type],
    queryFn: async () => {
      const [entities, readableForms, groups] = await Promise.all([
        fetchApi(`/entity_summaries?entity_type=eq.${encodeURIComponent(type)}`),
        fetchApi(`/readable_forms_by_type?entity_type=eq.${encodeURIComponent(type)}`),
        fetchApi(`/entity_groups_hierarchy?entity_type=eq.${encodeURIComponent(type)}`),
      ]);

      return {
        entities: entities.map(e => ({
          ...e,
          authors: new Set(e.authors),
          count: e.mention_count
        })),
        readableFormMap: new Map(readableForms.map(r => [r.normalized_entity_text, r])),
        groupMap: new Map(groups.map(g => [g.readable_form, g])),
      };
    },
    enabled: !!type,
  });
}

// Helper function to chunk array into smaller batches
function chunkArray(array, size) {
  const chunks = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

// Helper function to make batched API requests
async function fetchBatched(endpoint, items, batchSize = 50) {
  const chunks = chunkArray(items, batchSize);
  const results = await Promise.all(
    chunks.map(chunk =>
      fetchApi(`${endpoint}(${chunk.map(item => encodeURIComponent(item)).join(',')})`)
    )
  );
  return results.flat();
}

export function useEntityDetails(type, entity, page = 1, pageSize = 5) {
  return useQuery({
    queryKey: ['entity', type, entity, page, pageSize],
    queryFn: async () => {
      console.log('useEntityDetails: Starting fetch for', { type, entity, page, pageSize });

      // First fetch the basic entity data
      const [entitySummary, readableForms, groups] = await Promise.all([
        fetchApi(`/entity_summaries?entity_type=eq.${encodeURIComponent(type)}&or=(readable_form.eq.${encodeURIComponent(entity)},entity_text.eq.${encodeURIComponent(entity.toLowerCase())})`),
        fetchApi(`/readable_forms_lookup?entity_type=eq.${encodeURIComponent(type)}`),
        fetchApi(`/entity_groups_hierarchy?entity_type=eq.${encodeURIComponent(type)}`),
      ]);

      console.log('useEntityDetails: Initial data fetched', {
        entitySummaryCount: entitySummary.length,
        readableFormsCount: readableForms.length,
        groupsCount: groups.length,
        rawEntitySummary: entitySummary
      });

      // Then fetch the case studies for the current page only
      let entityData = entitySummary;
      if (entitySummary.length > 0) {
        // Get all unique authors from summaries
        const allAuthors = [...new Set(entitySummary.flatMap(e => Array.from(e.authors)))];

        // Calculate total mentions (one per author per entity)
        const totalMentions = entitySummary.reduce((sum, e) => sum + Array.from(e.authors).length, 0);

        // Calculate pagination
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;

        // Get authors for current page only
        const paginatedAuthors = allAuthors.slice(startIndex, endIndex);

        console.log('useEntityDetails: Authors to fetch case studies for:', {
          totalAuthors: allAuthors.length,
          currentPageAuthors: paginatedAuthors.length,
          page,
          startIndex,
          endIndex
        });

        // Fetch case studies for current page only
        const caseStudies = await fetchBatched(
          '/author_case_studies?author=in.',
          paginatedAuthors,
          50  // Adjust batch size as needed
        );

        console.log('useEntityDetails: Case studies fetched:', {
          requestedAuthors: paginatedAuthors,
          fetchedCaseStudies: caseStudies.length,
          caseStudyAuthors: caseStudies.map(cs => cs.author)
        });

        // Create a map of author to case study for current page
        const caseStudyMap = new Map(caseStudies.map(cs => [cs.author, cs.case_study_html]));

        // Map the case studies to their respective entities for current page only
        entityData = entitySummary.flatMap(e => {
          // Get intersection of current page authors and entity authors
          const currentPageAuthors = Array.from(e.authors).filter(author => paginatedAuthors.includes(author));

          // Create entries only for current page authors
          return currentPageAuthors.map(author => {
            const caseStudy = caseStudyMap.get(author);
            console.log('Mapping entity to case study:', {
              author,
              hasCaseStudy: !!caseStudy,
              entityText: e.entity_text,
              readableForm: e.readable_form
            });
            return {
              ...e,
              authors: new Set([author]), // Set with just this author
              count: 1, // Individual mention count
              case_study: caseStudy
            };
          });
        });

        console.log('useEntityDetails: Processed entity data:', {
          totalMentions,
          currentPageMentions: entityData.length,
          sampleEntry: entityData[0]
        });

        return {
          totalMentions,
          entityData,
          readableFormMap: new Map(readableForms.map(r => [r.normalized_entity_text, r])),
          groupMap: new Map(groups.map(g => [g.readable_form, g])),
          rawEntitySummary: entitySummary,
          pagination: {
            currentPage: page,
            totalPages: Math.ceil(totalMentions / pageSize),
            pageSize
          }
        };
      }

      return {
        totalMentions: 0,
        entityData: [],
        readableFormMap: new Map(readableForms.map(r => [r.normalized_entity_text, r])),
        groupMap: new Map(groups.map(g => [g.readable_form, g])),
        rawEntitySummary: entitySummary,
        pagination: {
          currentPage: page,
          totalPages: 0,
          pageSize
        }
      };
    },
    enabled: !!type && !!entity,
  });
}

export function useCaseStudy(author) {
  return useQuery({
    queryKey: ['caseStudy', author],
    queryFn: () => fetchApi(`/author_case_studies?author=eq.${encodeURIComponent(author)}&select=*,case_study_html`),
    enabled: !!author,
  });
}

export function useTopicPage(topic) {
  return useQuery({
    queryKey: ['topicPage', topic],
    queryFn: () => {
      if (!topic) return Promise.resolve(null);
      return fetchApi(`/topic_pages?topic=eq.${encodeURIComponent(topic)}&select=*`);
    },
    enabled: !!topic,
  });
}

export function useTopicPagesByGroup(groups) {
  return useQuery({
    queryKey: ['topicPagesByGroup', groups],
    queryFn: async () => {
      if (!groups || groups.length === 0) {
        console.log('No groups provided to useTopicPagesByGroup');
        return [];
      }

      try {
        // Create conditions for each group
        const groupConditions = groups.map(group => {
          // PostgreSQL requires % to be unescaped in LIKE patterns
          // but we still need to escape special characters in the group name itself
          const escapedGroup = group.replace(/[%_]/g, '\\$&');
          return [
            // Exact match for the group itself
            `topic.eq.${encodeURIComponent(escapedGroup)}`,
            // LIKE pattern for subgroups, using ilike for case-insensitive matching
            `topic.ilike.${encodeURIComponent(escapedGroup + '%')}`,
            // Also match when group is in the middle (for cases like "Other - Group Name")
            `topic.ilike.${encodeURIComponent('% - ' + escapedGroup + '%')}`
          ];
        }).flat();

        // Join conditions with comma for the API
        const queryString = `/topic_pages?or=(${groupConditions.join(',')})&select=id,topic,content_html,content,source_count,updated_at`;
        console.log('Fetching topic pages with query:', queryString);

        const response = await fetchApi(queryString);
        console.log(`Received ${response?.length || 0} topic pages:`, response);
        return response || [];
      } catch (error) {
        console.error('Error fetching topic pages:', error);
        throw error;
      }
    },
    enabled: !!groups && groups.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

export function useStatistics() {
  return useQuery({
    queryKey: ['statistics'],
    queryFn: async () => {
      const [
        messageStats,
        authorStats,
        caseStudyStats,
        dateHistogram
      ] = await Promise.all([
        // Get message statistics including counts by source
        fetchApi('/rpc/message_stats'),
        // Get author statistics including discussion counts and relevance scores
        fetchApi('/rpc/author_stats'),
        // Get case study statistics
        fetchApi('/rpc/case_study_stats'),
        // Get date histogram data
        fetchApi('/rpc/message_date_histogram')
      ]);

      return {
        messageStats: {
          totalCount: messageStats.total_count,
          messageTypes: messageStats.message_types,
          sourceTypes: messageStats.source_types,
          redditCount: messageStats.reddit_count,
          poisCenterCount: messageStats.poiscenter_count,
          dateRange: {
            earliest: messageStats.earliest_date,
            latest: messageStats.latest_date
          },
          dateHistogram: {
            bins: dateHistogram.bins,
            startDate: dateHistogram.start_date,
            endDate: dateHistogram.end_date
          }
        },
        authorStats: {
          totalDiscussions: authorStats.total_discussions,
          uniqueAuthors: authorStats.unique_authors,
          relevanceHistogram: {
            bins: authorStats.relevance_histogram,
            totalScores: authorStats.total_scores
          },
          topAuthors: authorStats.top_authors
        },
        caseStudyStats: {
          totalCount: caseStudyStats.total_count,
          avgRelevanceScore: caseStudyStats.avg_relevance_score,
          avgEntityCount: caseStudyStats.avg_entity_count
        }
      };
    }
  });
}

export function useAuthorDiscussions(author, page = 1, pageSize = 10) {
  return useQuery({
    queryKey: ['authorDiscussions', author, page, pageSize],
    queryFn: async () => {
      // Calculate offset
      const offset = (page - 1) * pageSize;

      // Get total count first
      const countResponse = await fetchApi(`/author_discussions_with_urls?author=eq.${encodeURIComponent(author)}&select=count`);
      const totalCount = parseInt(countResponse[0]?.count || '0');

      // Get paginated data
      const data = await fetchApi(
        `/author_discussions_with_urls?author=eq.${encodeURIComponent(author)}` +
        `&order=relevance_score.desc` +
        `&limit=${pageSize}` +
        `&offset=${offset}`
      );

      return {
        discussions: data,
        totalCount,
        totalPages: Math.ceil(totalCount / pageSize),
        currentPage: page,
      };
    },
    enabled: !!author,
  });
}

// Add other API hooks as needed...